import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useT } from '../i18n';

// The one filter control used in every page toolbar (see .toolbar in the
// design kit). A fixed-size trigger — "Label: value ▾" — that never grows with
// the selection ("AE44 +2"), and a popover with an optional search and the
// options (checkboxes when several can be picked). Keyboard: ↑/↓ to move,
// Enter/Space to pick, Esc to close.

export interface FilterOption<T extends string | number> { value: T; label: string; sub?: string }

const SEARCH_FROM = 8;   // show the search field from this many options

function matches(query: string, text: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  let i = 0;                         // subsequence match: "ae4" finds "AE44 — …"
  for (const ch of t) { if (ch === q[i]) i++; if (i === q.length) return true; }
  return false;
}

export function FilterSelect<T extends string | number>({
  label, options, selected, onChange, multiple = true, emptyLabel, clearable = true, width, neutral,
}: {
  label: string;
  options: FilterOption<T>[];
  selected: T[];
  onChange: (values: T[]) => void;
  /** Several values (checkboxes) or exactly one. */
  multiple?: boolean;
  /** What the trigger says when nothing is selected, e.g. "Tous". */
  emptyLabel?: string;
  /** Show the × that empties the selection (off for a single choice that always has a value). */
  clearable?: boolean;
  /** Trigger max width in px (default 260). */
  width?: number;
  /** The default value of a single choice: while it's selected, the filter doesn't look applied. */
  neutral?: T;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(0);
  const [alignRight, setAlignRight] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const chosen = useMemo(() => options.filter(o => selected.includes(o.value)), [options, selected]);
  const shown = useMemo(() => options.filter(o => matches(query, `${o.label} ${o.sub ?? ''}`)), [options, query]);
  const active = chosen.length > 0 && !(neutral !== undefined && selected.length === 1 && selected[0] === neutral);

  const close = () => { setOpen(false); setQuery(''); };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Open towards the side with room, so the popover never runs off the screen.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    setAlignRight(r.left + 300 > window.innerWidth - 12);
    setFocus(0);
    searchRef.current?.focus();
  }, [open]);

  useEffect(() => { setFocus(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${focus}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [focus]);

  const pick = (v: T) => {
    if (multiple) onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
    else { onChange([v]); close(); }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setFocus(f => Math.min(f + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocus(f => Math.max(f - 1, 0)); }
    else if (e.key === 'Enter' || (e.key === ' ' && !query)) { e.preventDefault(); if (shown[focus]) pick(shown[focus].value); }
    else if (e.key === 'Tab') close();
  };

  let value: ReactNode = <span className="fsel-value is-empty">{emptyLabel ?? t('filters.all')}</span>;
  if (chosen.length >= 1) {
    value = (
      <>
        <span className="fsel-value">{chosen[0].label}</span>
        {chosen.length > 1 && <span className="fsel-more">+{chosen.length - 1}</span>}
      </>
    );
  }

  return (
    <div className={`fsel${active ? ' is-active' : ''}${open ? ' is-open' : ''}`} ref={rootRef} onKeyDown={onKey}>
      <button
        type="button"
        className="fsel-trigger"
        style={width ? { maxWidth: width } : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        title={chosen.length > 1 ? chosen.map(o => o.label).join('\n') : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="fsel-label">{label}</span>
        {value}
        <ChevronDown size={14} className="fsel-chevron" aria-hidden />
      </button>
      {active && clearable ? (
        <button type="button" className="fsel-clear" aria-label={t('filters.clear')} title={t('filters.clear')}
          onClick={() => { onChange([]); close(); }}>
          <X size={12} />
        </button>
      ) : null}

      {open && (
        <div className={`fsel-panel${alignRight ? ' is-right' : ''}`}>
          {options.length >= SEARCH_FROM && (
            <div className="fsel-search">
              <Search size={13} aria-hidden />
              <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={t('filters.searchPlaceholder')} aria-label={t('filters.searchPlaceholder')} aria-controls={listId} />
            </div>
          )}
          <ul className="fsel-list" id={listId} role="listbox" aria-multiselectable={multiple} aria-label={label} ref={listRef}
            tabIndex={options.length >= SEARCH_FROM ? -1 : 0}>
            {shown.length === 0 && <li className="fsel-empty">{t('filters.noMatch')}</li>}
            {shown.map((o, i) => {
              const on = selected.includes(o.value);
              return (
                <li key={String(o.value)} role="option" aria-selected={on} data-index={i}
                  className={`fsel-option${on ? ' is-selected' : ''}${i === focus ? ' is-focused' : ''}`}
                  onMouseEnter={() => setFocus(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o.value)}>
                  <span className={multiple ? 'fsel-box' : 'fsel-tick'} aria-hidden>{on && <Check size={12} strokeWidth={3} />}</span>
                  <span className="fsel-option-label">{o.label}</span>
                  {o.sub && <span className="fsel-option-sub">{o.sub}</span>}
                </li>
              );
            })}
          </ul>
          {multiple && chosen.length > 0 && (
            <div className="fsel-footer">
              <span>{t('filters.selectedCount', { n: chosen.length })}</span>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => onChange([])}>{t('filters.clear')}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** An on/off filter in the same look and height as FilterSelect ("Mes tâches", "Seulement modifiés"). */
export function FilterToggle({ label, checked, onChange, title }: { label: string; checked: boolean; onChange: (v: boolean) => void; title?: string }) {
  return (
    <button type="button" className={`fsel-toggle${checked ? ' is-active' : ''}`} aria-pressed={checked} title={title}
      onClick={() => onChange(!checked)}>
      <span className="fsel-box" aria-hidden>{checked && <Check size={12} strokeWidth={3} />}</span>
      {label}
    </button>
  );
}

/** "Réinitialiser" — shown when any filter of the toolbar is set. */
export function FilterReset({ onClick }: { onClick: () => void }) {
  const { t } = useT();
  return <button type="button" className="fsel-reset" onClick={onClick}>{t('filters.reset')}</button>;
}
